import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MapPin, Package, Truck, Clock, Camera, DollarSign, Loader2 } from 'lucide-react';
import MapView from '@/components/MapView';
import { getLoadStatusClasses } from '@/lib/statusColors';

// Haversine distance helper
const getDistanceKm = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const STATUS_LABELS = {
  draft: 'Draft', posted: 'Posted', assigned: 'Assigned', at_pickup: 'At Pickup',
  in_transit: 'In Transit', at_delivery: 'At Delivery', completed: 'Completed', invoiced: 'Invoiced',
};

export default function LoadDetailModal({ open, onOpenChange, loadId }) {
  const { token } = useAuth();
  const [load, setLoad] = useState(null);
  const [charges, setCharges] = useState([]);
  const [pod, setPod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('details');
  const [liveDriverLoc, setLiveDriverLoc] = useState(null);
  const driverPollRef = useRef(null);

  useEffect(() => {
    if (!open || !loadId) return;
    setLoading(true);
    setLiveDriverLoc(null);
    Promise.all([
      api.get(`/loads/${loadId}`),
      api.get(`/loads/${loadId}/charges`).catch(() => ({ data: [] })),
      api.get(`/loads/${loadId}/pod`).catch(() => ({ data: null })),
    ]).then(([loadRes, chargesRes, podRes]) => {
      setLoad(loadRes.data);
      setCharges(chargesRes.data || []);
      setPod(podRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [open, loadId, token]);

  // Live driver GPS polling every 15 seconds
  useEffect(() => {
    if (driverPollRef.current) clearInterval(driverPollRef.current);
    if (!open || !load?.driver_id) return;
    let active = true;
    const poll = () => {
      api.get('/tracking/latest')
        .then(res => {
          if (!active) return;
          // Find this driver's location from the tracking list
          const entries = Array.isArray(res.data) ? res.data : [res.data];
          const match = entries.find(e => e?.driver_id === load.driver_id);
          if (match?.lat) setLiveDriverLoc(match);
        })
        .catch(() => {});
    };
    poll();
    driverPollRef.current = setInterval(poll, 15000);
    return () => { active = false; clearInterval(driverPollRef.current); };
  }, [open, load?.driver_id]);

  if (!open) return null;

  const chargeTotals = charges.reduce((a, c) => ({
    client: a.client + (parseFloat(c.client_charges) || 0),
    tax: a.tax + (parseFloat(c.gst) || 0) + (parseFloat(c.hst) || 0) + (parseFloat(c.pst) || 0),
  }), { client: 0, tax: 0 });

  const isPickupPhase = ['assigned', 'at_pickup'].includes(load?.status);
  const etaMin = (liveDriverLoc?.lat && load?.pickup_lat)
    ? Math.round((getDistanceKm(
        liveDriverLoc.lat, liveDriverLoc.lng,
        isPickupPhase ? load.pickup_lat : load.delivery_lat,
        isPickupPhase ? load.pickup_lng : load.delivery_lng
      ) / 80) * 60)
    : null;

  const detailsMapMarkers = [];
  if (load) {
    if (load.pickup_lat) detailsMapMarkers.push({ id: 'p', lat: load.pickup_lat, lng: load.pickup_lng, label: load.pickup_name || 'Pickup', type: 'pickup' });
    if (load.delivery_lat) detailsMapMarkers.push({ id: 'd', lat: load.delivery_lat, lng: load.delivery_lng, label: load.delivery_name || 'Delivery', type: 'delivery' });
    if (liveDriverLoc?.lat) detailsMapMarkers.push({ id: 't', lat: liveDriverLoc.lat, lng: liveDriverLoc.lng, label: 'Driver', type: 'truck', color: '#E5A100' });
  }
  const detailsMapLoads = load ? [{
    pickup_lat: load.pickup_lat, pickup_lng: load.pickup_lng,
    delivery_lat: load.delivery_lat, delivery_lng: load.delivery_lng,
    driver_lat: liveDriverLoc?.lat || null,
    driver_lng: liveDriverLoc?.lng || null,
  }] : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border text-foreground max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-muted-foreground" />
            {load ? `Load ${load.load_id}` : 'Load Details'}
            {load && <Badge className={`${getLoadStatusClasses(load.status)} text-xs ml-2`}>{STATUS_LABELS[load.status]}</Badge>}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : load && (
          <Tabs value={tab} onValueChange={setTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="bg-muted border-b border-input h-9">
              <TabsTrigger value="details" className="text-xs data-[state=active]:bg-zinc-700 dark:data-[state=active]:bg-zinc-700 data-[state=active]:text-foreground">Details</TabsTrigger>
              <TabsTrigger value="tracking" className="text-xs data-[state=active]:bg-zinc-700 dark:data-[state=active]:bg-zinc-700 data-[state=active]:text-foreground">Tracking</TabsTrigger>
              <TabsTrigger value="charges" className="text-xs data-[state=active]:bg-zinc-700 dark:data-[state=active]:bg-zinc-700 data-[state=active]:text-foreground">Charges</TabsTrigger>
              <TabsTrigger value="pod" className="text-xs data-[state=active]:bg-zinc-700 dark:data-[state=active]:bg-zinc-700 data-[state=active]:text-foreground">Proof of Delivery</TabsTrigger>
            </TabsList>

            <div className="flex-1 overflow-auto">
              <TabsContent value="details" className="mt-0 p-4 space-y-4">
                {/* Map */}
                {load.pickup_lat && load.delivery_lat && (
                  <div className="h-[300px] rounded-lg overflow-hidden border border-input relative">
                    <MapView
                      markers={detailsMapMarkers}
                      loads={detailsMapLoads}
                      dark={false} zoom={6} showSatelliteToggle showLegend
                    />
                    {etaMin !== null && ['assigned', 'at_pickup', 'in_transit'].includes(load.status) && (
                      <div className="absolute top-3 right-3 bg-card/90 backdrop-blur border border-border rounded-lg px-3 py-2 flex items-center gap-2 shadow-lg z-10">
                        <Truck className="w-4 h-4 text-brand-accent" />
                        <span className="text-xs font-medium text-foreground">
                          Driver ~{etaMin} min away{isPickupPhase ? ' from pickup' : ' from delivery'}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                    <p className="text-[10px] font-bold text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3 text-emerald-500" /> PICKUP</p>
                    <p className="text-sm font-medium">{load.pickup_name || 'N/A'}</p>
                    <p className="text-xs text-muted-foreground">{load.pickup_address}</p>
                    {load.shipper_contact_name && <p className="text-xs text-muted-foreground">{load.shipper_contact_name} {load.shipper_contact_phone}</p>}
                  </div>
                  <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                    <p className="text-[10px] font-bold text-muted-foreground flex items-center gap-1"><MapPin className="w-3 h-3 text-red-500" /> DELIVERY</p>
                    <p className="text-sm font-medium">{load.delivery_name || 'N/A'}</p>
                    <p className="text-xs text-muted-foreground">{load.delivery_address}</p>
                    {load.receiver_contact_name && <p className="text-xs text-muted-foreground">{load.receiver_contact_name} {load.receiver_contact_phone}</p>}
                  </div>
                </div>
                <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                  <p className="text-[10px] font-bold text-muted-foreground flex items-center gap-1"><Package className="w-3 h-3" /> CARGO</p>
                  <p className="text-sm">{load.cargo_description}</p>
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <span>{(load.weight_lbs || 0).toLocaleString()} lbs</span>
                    <span>{Math.round((load.distance_miles || 0) * 1.60934)} km</span>
                    <span>${Number(load.rate || 0).toFixed(2)}</span>
                    {load.equipment_type_required && <span><Truck className="w-3 h-3 inline" /> {load.equipment_type_required}</span>}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="tracking" className="mt-0 p-4 space-y-4">
                {/* Live driver map in tracking tab */}
                {liveDriverLoc?.lat && load.pickup_lat && load.delivery_lat && (
                  <div className="space-y-2">
                    <div className="h-[200px] rounded-lg overflow-hidden border border-input relative">
                      <MapView
                        markers={detailsMapMarkers}
                        loads={detailsMapLoads}
                        dark={false} zoom={7} showSatelliteToggle
                      />
                    </div>
                    {etaMin !== null && ['assigned', 'at_pickup', 'in_transit'].includes(load.status) && (
                      <div className="flex items-center gap-2 text-xs bg-brand-accent/10 text-brand-accent rounded-md px-3 py-2">
                        <Truck className="w-4 h-4" />
                        <span className="font-medium">Driver ~{etaMin} min away{isPickupPhase ? ' from pickup' : ' from delivery'}</span>
                        <span className="text-muted-foreground ml-auto text-[10px]">Updated every 15s</span>
                      </div>
                    )}
                  </div>
                )}
                <ScrollArea className="h-[300px]">
                  <div className="space-y-0">
                    {(load.status_history || []).slice().reverse().map((h, i) => (
                      <div key={i} className="flex gap-3 pb-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full flex-shrink-0 ${i === 0 ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`} />
                          {i < (load.status_history?.length || 0) - 1 && <div className="w-px flex-1 bg-border mt-1" />}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground">{STATUS_LABELS[h.status] || h.status}</span>
                            <span className="text-[10px] text-muted-foreground">{h.by_name || h.by || 'System'}</span>
                          </div>
                          <p className="text-[10px] text-muted-foreground">{new Date(h.timestamp).toLocaleString()}</p>
                          {h.notes && <p className="text-xs text-muted-foreground italic mt-0.5">{h.notes}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="charges" className="mt-0 p-4">
                {charges.length > 0 ? (
                  <div className="space-y-2">
                    {charges.filter(c => (parseFloat(c.client_charges) || 0) > 0).map(c => (
                      <div key={c.charge_id} className="flex justify-between items-center py-2 border-b border-border">
                        <div>
                          <p className="text-sm text-foreground">{c.description || c.charge_type}</p>
                          {c.quantity > 1 && <p className="text-[10px] text-muted-foreground">{c.quantity} x ${Number(c.client_rate).toFixed(2)}</p>}
                        </div>
                        <span className="text-sm font-medium">${Number(c.client_charges).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="pt-3 space-y-1">
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">Subtotal</span><span>${chargeTotals.client.toFixed(2)}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-muted-foreground">Tax</span><span>${chargeTotals.tax.toFixed(2)}</span></div>
                      <div className="flex justify-between text-sm font-bold border-t border-input pt-2">
                        <span>Total</span><span>${(chargeTotals.client + chargeTotals.tax).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                ) : <p className="text-muted-foreground text-center py-8">No charges recorded</p>}
              </TabsContent>

              <TabsContent value="pod" className="mt-0 p-4">
                {pod ? (
                  <div className="space-y-4">
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-[10px] font-bold text-muted-foreground mb-1">RECEIVER</p>
                      <p className="text-sm font-medium">{pod.receiver_name}</p>
                      <p className="text-[10px] text-muted-foreground">{new Date(pod.delivered_at).toLocaleString()}</p>
                    </div>
                    {pod.signature_url && (
                      <div>
                        <p className="text-[10px] font-bold text-muted-foreground mb-1">SIGNATURE</p>
                        <img src={pod.signature_url} alt="Signature" className="w-full h-24 object-contain bg-muted/50 rounded-lg border border-input" />
                      </div>
                    )}
                    {pod.photo_urls?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-bold text-muted-foreground mb-1">PHOTOS ({pod.photo_urls.length})</p>
                        <div className="grid grid-cols-3 gap-2">
                          {pod.photo_urls.map((url, i) => (
                            <img key={i} src={url} alt={`POD ${i + 1}`} className="w-full h-24 object-cover rounded-lg border border-input" />
                          ))}
                        </div>
                      </div>
                    )}
                    {pod.notes && (
                      <div>
                        <p className="text-[10px] font-bold text-muted-foreground mb-1">NOTES</p>
                        <p className="text-sm text-muted-foreground">{pod.notes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Camera className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">{load.status === 'completed' || load.status === 'invoiced' ? 'No proof of delivery submitted' : 'Delivery not yet completed'}</p>
                  </div>
                )}
              </TabsContent>
            </div>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
